import React, { useState } from 'react';
import './AccountManager.css';

const AccountManager = ({ maxAccounts, isUnlimited, onConfirm, onSkip, onBack, selectedPlan }) => {
  const [accounts, setAccounts] = useState(['']);
  const [errors, setErrors] = useState({});
  const [activePanel, setActivePanel] = useState('summary'); // summary, tips, privacy

  const validateUsername = (username) => {
    if (!username) return '';
    
    // Remove @ symbol if present
    const cleanUsername = username.replace('@', '');
    
    // Basic Twitter username validation
    if (!/^[a-zA-Z0-9_]{1,15}$/.test(cleanUsername)) {
      return 'Invalid username format';
    }
    
    return '';
  };

  const handleUsernameChange = (index, value) => {
    const newAccounts = [...accounts];
    newAccounts[index] = value;
    setAccounts(newAccounts);
    
    // Clear error for this field
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const addAccountField = () => {
    if (isUnlimited || accounts.length < maxAccounts) {
      setAccounts([...accounts, '']);
    }
  };

  const removeAccountField = (index) => {
    if (accounts.length > 1) {
      const newAccounts = accounts.filter((_, i) => i !== index);
      setAccounts(newAccounts);
      
      // Remove error for this field
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  const handleConfirm = () => {
    const newErrors = {};
    const validAccounts = [];
    
    accounts.forEach((username, index) => {
      if (username.trim()) {
        const cleanUsername = username.trim().replace('@', '');
        const error = validateUsername(cleanUsername);
        
        if (error) {
          newErrors[index] = error;
        } else {
          validAccounts.push(cleanUsername);
        }
      }
    });
    
    // Check for duplicates
    const uniqueAccounts = [...new Set(validAccounts)];
    if (uniqueAccounts.length !== validAccounts.length) {
      // Find duplicates and mark them
      const seen = new Set();
      validAccounts.forEach((username, index) => {
        if (seen.has(username)) {
          newErrors[index] = 'Duplicate username';
        }
        seen.add(username);
      });
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      onConfirm(uniqueAccounts);
    }
  };

  const canAddMore = isUnlimited || accounts.length < maxAccounts;
  const hasValidAccounts = accounts.some(username => username.trim() && !validateUsername(username.trim().replace('@', '')));
  const validAccountCount = accounts.filter(username => username.trim() && !validateUsername(username.trim().replace('@', ''))).length;

  // Get plan data - this should be passed from parent component
  const planData = selectedPlan || {
    name: 'Scale',
    accounts: maxAccounts,
    price: 200,
    planType: 'scale'
  };

  const renderRightPanel = () => {
    switch (activePanel) {
      case 'summary':
        return (
          <div className="order-summary">
            <h3>Order Summary</h3>
            <div className="summary-details">
              <div className="plan-info">
                <div className="plan-name">{planData.name} Plan</div>
                <div className="plan-accounts">
                  {isUnlimited ? 'Unlimited' : `Up to ${maxAccounts}`} Accounts
                </div>
              </div>
              
              <div className="price-breakdown">
                <div className="price-line">
                  <span>Monthly Subscription</span>
                  <span>${planData.price}</span>
                </div>
                <div className="price-line total">
                  <span>Total</span>
                  <span>${planData.price}/month</span>
                </div>
              </div>

              <div className="accounts-added">
                <h4>Accounts Added ({validAccountCount})</h4>
                {validAccountCount > 0 ? (
                  <div className="added-accounts-list">
                    {accounts
                      .filter(username => username.trim() && !validateUsername(username.trim().replace('@', '')))
                      .map((username, index) => (
                        <div key={index} className="added-account">
                          @{username.trim().replace('@', '')}
                        </div>
                      ))
                    }
                  </div>
                ) : (
                  <div className="no-accounts">No accounts added yet</div>
                )}
              </div>
            </div>
          </div>
        );
      
      case 'tips':
        return (
          <div className="tips-panel">
            <h3>📝 Quick Setup Tips</h3>
            <div className="tips-content">
              <div className="tip-item">
                <div className="tip-icon">💡</div>
                <div className="tip-text">Enter usernames without the @ symbol</div>
              </div>
              <div className="tip-item">
                <div className="tip-icon">⚙️</div>
                <div className="tip-text">You can add accounts later in settings</div>
              </div>
              <div className="tip-item">
                <div className="tip-icon">🔓</div>
                <div className="tip-text">Make sure accounts are publicly accessible</div>
              </div>
              <div className="tip-item">
                <div className="tip-icon">🎬</div>
                <div className="tip-text">Accounts will be used for content generation</div>
              </div>
            </div>
          </div>
        );
      
      case 'privacy':
        return (
          <div className="privacy-panel">
            <h3>🔒 Privacy & Security</h3>
            <div className="privacy-content">
              <div className="privacy-item">
                <div className="privacy-icon">👁️</div>
                <div className="privacy-text">
                  <strong>Public Data Only</strong>
                  <p>We only read public tweet data that's already visible to everyone.</p>
                </div>
              </div>
              <div className="privacy-item">
                <div className="privacy-icon">🚫</div>
                <div className="privacy-text">
                  <strong>No Credentials Stored</strong>
                  <p>Your account passwords are never stored or accessed by our system.</p>
                </div>
              </div>
              <div className="privacy-item">
                <div className="privacy-icon">🛡️</div>
                <div className="privacy-text">
                  <strong>Secure Processing</strong>
                  <p>All data is processed securely and used only for content generation.</p>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="account-manager-container">
      {/* Left Side - Scrollable Content */}
      <div className="left-panel">
        <div className="account-manager-header">
          <button className="back-btn" onClick={onBack}>
            ← Back to Plans
          </button>
          <div className="header-content">
            <h1>Add Your Twitter Accounts</h1>
            <p>
              {isUnlimited 
                ? 'Add as many Twitter accounts as you want' 
                : `Add up to ${maxAccounts} Twitter account${maxAccounts > 1 ? 's' : ''}`
              }
            </p>
          </div>
        </div>

        <div className="accounts-section">
          <div className="accounts-header">
            <h3>Twitter Usernames</h3>
            <span className="account-counter">
              {validAccountCount} / {isUnlimited ? '∞' : maxAccounts}
            </span>
          </div>

          <div className="accounts-list">
            {accounts.map((username, index) => (
              <div key={index} className="account-input-group">
                <div className="username-input-wrapper">
                  <span className="username-prefix">@</span>
                  <input
                    type="text"
                    placeholder="username"
                    value={username}
                    onChange={(e) => handleUsernameChange(index, e.target.value)}
                    className={`username-input ${errors[index] ? 'error' : ''}`}
                  />
                  {accounts.length > 1 && (
                    <button 
                      className="remove-account-btn"
                      onClick={() => removeAccountField(index)}
                    >
                      ×
                    </button>
                  )}
                </div>
                {errors[index] && (
                  <span className="input-error">{errors[index]}</span>
                )}
              </div>
            ))}
          </div>

          <div className="account-actions">
            {canAddMore && (
              <button className="add-account-btn" onClick={addAccountField}>
                <span>+ Add Another Account</span>
              </button>
            )}
            
            {!isUnlimited && accounts.length >= maxAccounts && (
              <div className="limit-notice">
                <span>🔒 Account limit reached for your plan</span>
              </div>
            )}
          </div>

          <div className="action-buttons">
            <button className="skip-btn" onClick={onSkip}>
              Skip for Now
            </button>
            <button 
              className="confirm-btn" 
              onClick={handleConfirm}
              disabled={Object.keys(errors).length > 0}
            >
              <span>Continue to Payment</span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>

      {/* Right Side - Static Sidebar */}
      <div className="right-sidebar">
        <div className="panel-tabs">
          <button 
            className={`panel-tab ${activePanel === 'summary' ? 'active' : ''}`}
            onClick={() => setActivePanel('summary')}
          >
            📋 Summary
          </button>
          <button 
            className={`panel-tab ${activePanel === 'tips' ? 'active' : ''}`}
            onClick={() => setActivePanel('tips')}
          >
            💡 Tips
          </button>
          <button 
            className={`panel-tab ${activePanel === 'privacy' ? 'active' : ''}`}
            onClick={() => setActivePanel('privacy')}
          >
            🔒 Privacy
          </button>
        </div>

        <div className="panel-content">
          {renderRightPanel()}
        </div>
      </div>
    </div>
  );
};

export default AccountManager; 